/**
 * SliceNavigator - Vertical slice navigation component
 *
 * Positioned on the right edge of each viewport, provides:
 * - Up/Down arrow buttons for single-slice navigation
 * - Vertical slider for quick scrubbing through slices
 * - Slice counter showing current/total
 *
 * Semi-transparent design that doesn't obstruct the image.
 */

import React, { useCallback, useRef, useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { FaChevronUp, FaChevronDown } from "../../utils/icons";
import { keyboardHintsShownAtom } from "../../atoms/uiAtoms";
import { KeyboardHint } from "./KeyboardHint";

export interface SliceNavigatorProps {
  /** The slot's current list-index — single source of truth. Drives the
   *  thumb position. Writers (wheel/drag/↑↓/AI jump/init) all write the
   *  atom directly, so the atom is always the truth. */
  sliceIndex: number;
  totalSlices: number;
  onSliceChange: (index: number) => void;
  isActive?: boolean;
  /** Slice indices that have annotations (shown as purple ticks on the track). */
  annotatedSlices?: number[];
  /**
   * Slice indices carrying AI segmentation marks (shown as signal-blue ticks). A
   * mask-localized CADe finding reports one key slice per segment; several
   * segments can share a slice, so this list is already de-duplicated by the
   * caller and the per-slice count is surfaced by SegmentStrip, not here.
   */
  segmentationSlices?: number[];
  /** Slice index where nnInteractive segmentation is projected (orange marker). */
  projectedSlice?: number | null;
  /**
   * Per-series list-index direction sign in patient coords (from IPP).
   *   +1 → increasing list-index = scroll-up (cranial/posterior/patient-left). Track top = list-index N-1.
   *   -1 → decreasing list-index = scroll-up. Track top = list-index 0 (default convention).
   *    0 → unknown; fall back to default convention (-1 behavior).
   * Inverts the thumb mapping, ↑/↓ buttons, annotated/projected tick positions,
   * and the progress fill so "thumb up" matches the per-plane scroll-up direction.
   */
  listAnatomicSign?: -1 | 0 | 1;
}

export const SliceNavigator: React.FC<SliceNavigatorProps> = ({
  sliceIndex,
  totalSlices,
  onSliceChange,
  isActive = false,
  annotatedSlices,
  segmentationSlices,
  projectedSlice,
  listAnatomicSign = 0,
}) => {
  // When `listAnatomicSign === 1`, increasing list-index moves anatomically up,
  // so the track must be inverted (top of bar = highest index) to keep
  // "thumb at top = anatomic-up". Sign 0 or -1 uses the default convention.
  const inverted = listAnatomicSign === 1;
  const indexFromProgress = (p: number) => Math.round((inverted ? 1 - p : p) * (totalSlices - 1));
  const progressFromIndex = (i: number) => {
    if (totalSlices <= 1) return 0;
    const raw = i / (totalSlices - 1);
    return inverted ? 1 - raw : raw;
  };
  // "Up" button always moves anatomically up — when inverted that means
  // incrementing the list-index; otherwise decrementing.
  const upStep = inverted ? +1 : -1;
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showKeyboardHint, setShowKeyboardHint] = useState(false);
  const hintsShown = useAtomValue(keyboardHintsShownAtom);
  const trackRef = useRef<HTMLDivElement>(null);
  const hintShownRef = useRef(false);
  // Pixel offset between the cursor and the thumb center at grab time. Subtracted
  // during drag so the thumb stays under the cursor instead of snapping its
  // center to the pointer on the first move (the "goes down when I pull up" bug).
  const dragOffsetRef = useRef(0);

  // Show controls when hovering, dragging, or when viewport is active
  const showControls = isHovering || isDragging || isActive;

  // Show keyboard hint on first hover (if not already shown globally)
  useEffect(() => {
    if (isHovering && !hintsShown && !hintShownRef.current) {
      setShowKeyboardHint(true);
      hintShownRef.current = true;
    }
  }, [isHovering, hintsShown]);

  // Calculate thumb position (0 = top, 1 = bottom)
  const progress = progressFromIndex(sliceIndex);

  // Handle up button click — anatomic up regardless of list direction
  const handleUp = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const next = sliceIndex + upStep;
      if (next >= 0 && next < totalSlices) {
        onSliceChange(next);
      }
    },
    [sliceIndex, totalSlices, onSliceChange, upStep],
  );

  // Handle down button click
  const handleDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const next = sliceIndex - upStep;
      if (next >= 0 && next < totalSlices) {
        onSliceChange(next);
      }
    },
    [sliceIndex, totalSlices, onSliceChange, upStep],
  );

  // Calculate slice index from mouse position
  const calculateSliceFromPosition = useCallback(
    (clientY: number) => {
      if (!trackRef.current || totalSlices <= 1) return sliceIndex;

      const rect = trackRef.current.getBoundingClientRect();
      const y = clientY - rect.top;
      const percentage = Math.max(0, Math.min(1, y / rect.height));
      return indexFromProgress(percentage);
    },
    [totalSlices, sliceIndex, indexFromProgress],
  );

  // Handle track click for direct positioning
  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const newIndex = calculateSliceFromPosition(e.clientY);
      if (newIndex !== sliceIndex) {
        onSliceChange(newIndex);
      }
    },
    [calculateSliceFromPosition, sliceIndex, onSliceChange],
  );

  // Handle drag start on thumb — record where on the thumb the cursor grabbed
  // (offset from the thumb center) so the drag tracks the pointer instead of
  // snapping the thumb center to it.
  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const rect = trackRef.current?.getBoundingClientRect();
      dragOffsetRef.current = rect ? e.clientY - (rect.top + progress * rect.height) : 0;
      setIsDragging(true);
    },
    [progress],
  );

  // Handle mouse move during drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Subtract the grab offset so the thumb follows the cursor from where it
      // was grabbed (no snap-to-center, which inverted short upward drags).
      const newIndex = calculateSliceFromPosition(e.clientY - dragOffsetRef.current);
      if (newIndex !== sliceIndex) {
        onSliceChange(newIndex);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, calculateSliceFromPosition, sliceIndex, onSliceChange]);

  // Don't render if no slices
  if (totalSlices <= 1) {
    return null;
  }

  return (
    <div
      className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-40"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Up arrow button — anatomic up (cranial/anterior/right) */}
      <button
        onClick={handleUp}
        disabled={sliceIndex + upStep < 0 || sliceIndex + upStep >= totalSlices}
        className={`
          p-0.5 rounded transition-all duration-200
          ${showControls ? "opacity-100" : "opacity-40"}
          ${sliceIndex + upStep < 0 || sliceIndex + upStep >= totalSlices ? "text-fg-faint cursor-not-allowed" : "text-fg-muted hover:text-fg hover:bg-white/10"}
        `}
        title="Previous slice (↑)"
      >
        <FaChevronUp className="w-3 h-3" />
      </button>

      {/* Vertical slider track */}
      <div
        ref={trackRef}
        data-testid="slice-track"
        onClick={handleTrackClick}
        className={`
          relative w-1.5 h-32 rounded-full cursor-pointer
          transition-all duration-200
          ${showControls ? "bg-white/15 opacity-100" : "bg-white/10 opacity-40"}
          backdrop-blur-xs
        `}
      >
        {/* Progress fill (from top to thumb) - no transition for smooth scrolling.
            Neutral white (not signal-blue): the slider sits at the right edge of
            the viewport and must stay visible against the blue active-viewport
            border. It's a functional on-image indicator, exempt from one-hue. */}
        <div
          className="absolute top-0 left-0 right-0 bg-white/40 rounded-t-full"
          style={{ height: `${progress * 100}%` }}
        />

        {/* Annotated slice markers — purple ticks. Left half-width: a segment
            mark co-located on the same slice paints the right half, so
            neither tick paints over the other — the user's own annotation
            stays visible right alongside the AI mark instead of one hiding
            the other. */}
        {annotatedSlices?.map((s) => {
          const pct = progressFromIndex(s) * 100;
          return (
            <div
              key={s}
              className="absolute left-0 w-1/2 pointer-events-none"
              style={{ top: `${pct}%` }}
            >
              <div className="w-full h-[2px] rounded-full bg-purple-400 opacity-80" />
            </div>
          );
        })}

        {/* AI segmentation marks — signal-blue ticks. Right half-width, side-by-side
            with the purple annotation ticks (left half) so a slice carrying
            both stays visible as both colors instead of the later-rendered
            block painting over the earlier one. */}
        {segmentationSlices?.map((s) => {
          const pct = progressFromIndex(s) * 100;
          return (
            <div
              key={`segmentation-${s}`}
              data-testid="segmentation-tick"
              className="absolute right-0 w-1/2 pointer-events-none"
              style={{ top: `${pct}%` }}
            >
              <div className="w-full h-[2px] rounded-full bg-blue-400 opacity-90" />
            </div>
          );
        })}

        {/* Projected segmentation marker — orange tick */}
        {projectedSlice != null && totalSlices > 1 && (
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{ top: `${progressFromIndex(projectedSlice) * 100}%` }}
          >
            <div className="w-full h-[3px] rounded-full bg-orange-400 opacity-90" />
          </div>
        )}

        {/* Thumb - purple when annotated, orange when on projected slice, default otherwise */}
        <div
          data-testid="slice-thumb"
          onMouseDown={handleThumbMouseDown}
          className={`
            absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full cursor-grab
            transition-colors duration-75
            ${
              isDragging
                ? annotatedSlices?.includes(sliceIndex)
                  ? "bg-purple-500 scale-125 cursor-grabbing"
                  : projectedSlice === sliceIndex
                    ? "bg-orange-400 scale-125 cursor-grabbing"
                    : "bg-white scale-125 cursor-grabbing"
                : annotatedSlices?.includes(sliceIndex)
                  ? "bg-purple-500 hover:bg-purple-400 hover:scale-125"
                  : projectedSlice === sliceIndex
                    ? "bg-orange-400 hover:bg-orange-300 hover:scale-125"
                    : "bg-white/90 hover:bg-white hover:scale-125"
            }
            shadow-[0_1px_3px_rgba(0,0,0,0.55)]
          `}
          style={{
            top: `calc(${progress * 100}% - 6px)`,
          }}
        />
      </div>

      {/* Down arrow button — anatomic down */}
      <button
        onClick={handleDown}
        disabled={sliceIndex - upStep < 0 || sliceIndex - upStep >= totalSlices}
        className={`
          p-0.5 rounded transition-all duration-200
          ${showControls ? "opacity-100" : "opacity-40"}
          ${sliceIndex - upStep < 0 || sliceIndex - upStep >= totalSlices ? "text-fg-faint cursor-not-allowed" : "text-fg-muted hover:text-fg hover:bg-white/10"}
        `}
        title="Next slice (↓)"
      >
        <FaChevronDown className="w-3 h-3" />
      </button>

      {/* Slice counter — reserve the widest string (e.g. "129/129") + tabular
          digits so 9→10→100 never widens the box and shifts the track. The
          reserve is content (2·digits+1 ch) PLUS the px-1.5 padding (0.75rem):
          box-sizing is border-box, so a ch-only min-width let the longest
          string overflow and grow the box by exactly the padding. */}
      <div
        className={`
          text-[10px] font-mono px-1.5 py-0.5 rounded tabular-nums text-center
          transition-all duration-200
          ${showControls ? "opacity-100 bg-surface-2/80" : "opacity-40 bg-bg-2/60"}
          text-fg backdrop-blur-xs whitespace-nowrap
        `}
        style={{ minWidth: `calc(${String(totalSlices).length * 2 + 1}ch + 0.75rem)` }}
      >
        {sliceIndex + 1}/{totalSlices}
      </div>

      {/* Keyboard shortcut hint - uses shared KeyboardHint component */}
      <KeyboardHint
        shortcut="↑↓"
        description="scroll"
        position="left"
        fadeAfter={4000}
        show={showKeyboardHint}
      />
    </div>
  );
};

export default SliceNavigator;
